import { useState, useEffect, useRef } from "react";
import { PageTitle } from "../../components";
import {
  Button,
  Form,
  Input,
  message,
  Skeleton,
  Upload,
  Popconfirm,
} from "antd";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import { PlusOutlined, LoadingOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import ReactQuill from "react-quill";
import "quill/dist/quill.snow.css";
import { newsApi, type News } from "../../_helpers/apiService";

const formItemLayout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 14 },
};

function NewsDetail() {
  const [form] = Form.useForm();
  let { newsId } = useParams();
  const [value, setValue] = useState("");
  const [data, setData] = useState<News | null>(null);
  const [newsImage, setNewsImage] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [formLoading, setFormLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [newsImageUrl, setNewsImageUrl] = useState<string | null>();
  const [newsImageName, setNewsImageName] = useState<string | null>();
  const [imageUploading, setImageUploading] = useState(false);
  const userData = useSelector((state: any) => state.authentication.userData);
  const editorRef = useRef(null);

  useEffect(() => {
    if (newsId) {
      fetchNewsData();
    }
  }, [newsId]);

  const fetchNewsData = async () => {
    if (!newsId) return;

    setLoading(true);
    try {
      const response = await newsApi.getById(parseInt(newsId));
      const newsData = response.data;

      setData(newsData as any);
      form.setFieldsValue(newsData);
      if (newsData?.image_url) {
        setNewsImageUrl(newsData?.image_url);
        setNewsImageName(newsData?.image_name);
      }
      setValue(newsData.content || "");
    } catch (error: any) {
      message.error("Мэдээний мэдээлэл татахад алдаа гарлаа");
    } finally {
      setLoading(false);
    }
  };

  const onFinish = () => {
    // @ts-ignore
    const content =
      // @ts-ignore
      editorRef.current.getEditor().getLength() > 1
        ? // @ts-ignore
          editorRef.current.getEditor().getText().trim().length
        : 0;

    if (content <= 0) {
      setShowError(true);
      return;
    } else {
      setShowError(false);
    }
    form
      .validateFields()
      .then(async (values) => {
        if (newsId) {
          setFormLoading(true);
          try {
            await newsApi.update(parseInt(newsId), {
              title: values.title,
              description: values.description,
              content: value,
              author_id: userData?.id || null,
              author_name: `${userData?.first_name} ${userData?.last_name}`,
              author_image_url: userData?.profile_image_url || undefined,
              club_id: data?.club_id,
              image_url: newsImageUrl || undefined,
              image_name: newsImageName || undefined,
            });

            setFormLoading(false);
            message.success("Мэдээ амжилттай засагдлаа");
            fetchNewsData();
          } catch (error: any) {
            message.error("Мэдээ засахад алдаа гарлаа");
            setFormLoading(false);
          }
        }
      })
      .catch((info) => {
        setFormLoading(false);
      });
  };

  const uploadButton = (
    <div>
      {imageUploading ? <LoadingOutlined /> : <PlusOutlined />}
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  const customRequest = async ({ file, onSuccess, onError }: any) => {
    setImageUploading(true);
    try {
      const response = await newsApi.uploadImage(file);
      setNewsImageUrl(response.data.image_url);
      setNewsImageName(response.data.image_name);
      setNewsImage(file);
      message.success("Зураг амжилттай байршуулагдлаа");
      onSuccess();
    } catch (error) {
      message.error("Зураг байршуулахад алдаа гарлаа");
      onError(error);
    } finally {
      setImageUploading(false);
    }
  };

  const confirm = async (e: any) => {
    setNewsImageUrl(null);
    setNewsImageName(null);
    setNewsImage(null);
    message.success("Зураг устгагдлаа");
  };

  const cancel = (e: any) => {
    // Cancelled
  };

  return (
    <>
      {data ? (
        <PageTitle
          loading={loading}
          title={data?.title}
          right={null}
          breadcrumbItems={[
            { link: "/news", title: "Мэдээ" },
            { title: data?.title },
          ]}
        />
      ) : (
        ""
      )}
      <div className="page-content-wrapper">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="card">
                <div className="card-body">
                  <Skeleton loading={loading} active>
                    <Form
                      {...formItemLayout}
                      form={form}
                      onFinish={() => onFinish()}
                    >
                      <Form.Item
                        label="Нэр"
                        name="title"
                        rules={[{ required: true, message: "Нэр оруулна уу!" }]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item
                        label="Товч"
                        name="description"
                        rules={[
                          {
                            required: true,
                            message: "Товч мэдээлэл оруулна уу!",
                          },
                        ]}
                      >
                        <Input.TextArea />
                      </Form.Item>

                      <Form.Item
                        label="Мэдээний зураг"
                        valuePropName="newsImage"
                      >
                        {newsImageUrl ? (
                          <div className="delete-image">
                            <img
                              src={newsImageUrl}
                              alt="logo"
                              style={{ width: 150 }}
                            />
                            <Popconfirm
                              title="Мэдээний зураг устгах уу?"
                              onConfirm={confirm}
                              onCancel={cancel}
                              okText="Тийм"
                              cancelText="Үгүй"
                            >
                              <Button danger>Устгах</Button>
                            </Popconfirm>
                          </div>
                        ) : (
                          <Upload
                            beforeUpload={(file) => {
                              const isPNG =
                                file.type === "image/png" ||
                                file.type === "image/jpg" ||
                                file.type === "image/jpeg";
                              if (!isPNG) {
                                message.error(`Зургийн файл буруу байна`);
                              }
                              return isPNG || Upload.LIST_IGNORE;
                            }}
                            customRequest={customRequest}
                            fileList={newsImage ? [newsImage] : []}
                            listType="picture-card"
                          >
                            {uploadButton}
                          </Upload>
                        )}
                      </Form.Item>

                      <Form.Item
                        label="Мэдээлэл"
                        style={{ height: 200, marginBottom: 100 }}
                      >
                        <div style={{ minHeight: 200 }}>
                          <ReactQuill
                            ref={editorRef}
                            theme="snow"
                            value={value}
                            onChange={setValue}
                            style={{
                              maxHeight: 150,
                            }}
                          />
                        </div>
                        {showError && (
                          <span style={{ color: "red" }}>
                            Мэдээлэл оруулна уу
                          </span>
                        )}
                      </Form.Item>

                      <Form.Item wrapperCol={{ offset: 6, span: 16 }}>
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={formLoading}
                        >
                          Хадгалах
                        </Button>
                        <span
                          style={{
                            fontSize: 10,
                            marginLeft: 10,
                            fontStyle: "italic",
                            color: "#ccc",
                          }}
                        >
                          {(data as any)?.updated_at
                            ? dayjs((data as any).updated_at).format("YYYY-MM-DD HH:mm")
                            : ""}
                        </span>
                      </Form.Item>
                    </Form>
                  </Skeleton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
export default NewsDetail;
